import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Package, TrendingDown, AlertCircle, Loader2, RefreshCw,
  ShoppingCart, ClipboardList, FileText, Sparkles, Clock,
  Trash2, AlertTriangle, Info, CheckCircle2, ArrowUpRight, ArrowDownRight,
  Camera,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { VarianceAlertSettings } from "./VarianceAlertSettings";
import { InsightCard } from "./dashboard/InsightCard";
import { KpiCard } from "./dashboard/KpiCard";
import { RecentAutoEntriesCard } from "./dashboard/RecentAutoEntriesCard";
import AutoReorderDraftsBanner from "./dashboard/AutoReorderDraftsBanner";
import PendingReceiveBanner from "./dashboard/PendingReceiveBanner";
import { PriceHikesCard } from "./dashboard/PriceHikesCard";

const MOVEMENT_LABELS = {
  purchase: { label: "Compra", icon: ArrowUpRight, color: "text-emerald-700" },
  sale_theoretical: { label: "Venta", icon: ArrowDownRight, color: "text-muted-foreground" },
  count_adjust: { label: "Ajuste conteo", icon: ClipboardList, color: "text-amber-700" },
  waste: { label: "Merma", icon: Trash2, color: "text-red-700" },
  breakage: { label: "Rotura", icon: AlertTriangle, color: "text-red-700" },
  comp: { label: "Invitación", icon: Sparkles, color: "text-violet-600" },
  transfer: { label: "Traslado", icon: ArrowUpRight, color: "text-muted-foreground" },
  initial_balance: { label: "Saldo inicial", icon: Package, color: "text-muted-foreground" },
  manual_adjust: { label: "Ajuste manual", icon: RefreshCw, color: "text-amber-700" },
};

const ALERT_STYLES = {
  danger: "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 text-red-900 dark:text-red-100",
  warning: "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100",
  info: "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100",
};
const ALERT_ICONS = {
  danger: AlertTriangle,
  warning: AlertCircle,
  info: Info,
};

/**
 * Inventario Vivo — Dashboard principal.
 * Muestra 4 KPIs, alertas accionables, top 5 pérdidas y últimos movimientos.
 * Reutiliza las acciones existentes del módulo (Compras, Importar factura/CSV).
 */
export function InventoryDashboard({ restaurantId, onGoToProducts, onImportInvoice, onImportCsv, onOpenPurchases, onGoToCount, onRegisterWaste }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [valueRange, setValueRange] = useState("7d"); // "7d" | "30d"
  const [wasteRange, setWasteRange] = useState("7d"); // "7d" | "30d"
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(true);
  const [insightError, setInsightError] = useState(null);
  const [enrichingMissing, setEnrichingMissing] = useState(false);
  const [varianceOpen, setVarianceOpen] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const d = await api.request(`/restaurants/${restaurantId}/inventory/dashboard`);
      setData(d);
    } catch (e) {
      toast.error(e?.message || "Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  const loadInsight = useCallback(async (force = false) => {
    if (!restaurantId) return;
    setInsightLoading(true);
    setInsightError(null);
    try {
      const d = await api.request(
        `/restaurants/${restaurantId}/stock-movements/insight${force ? "?force=true" : ""}`
      );
      setInsight(d);
      if (force) toast.success("Análisis actualizado");
    } catch (e) {
      setInsightError(e?.message || "No se pudo generar el análisis");
    } finally {
      setInsightLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadInsight(false); }, [loadInsight]);

  const handleEnrichMissing = useCallback(async () => {
    if (!restaurantId || enrichingMissing) return;
    setEnrichingMissing(true);
    try {
      const res = await api.request(`/restaurants/${restaurantId}/inventory/enrich-missing`, { method: "POST" });
      if (res.enqueued > 0) {
        toast.success(`${res.enqueued} ingredientes encolados para enriquecimiento IA`, {
          description: "Sigue el progreso en el indicador de IA arriba.",
        });
      } else {
        toast.info(res.message || "Todos los ingredientes ya tienen datos completos");
      }
    } catch (e) {
      toast.error(e?.message || "No se pudieron encolar");
    } finally {
      setEnrichingMissing(false);
    }
  }, [restaurantId, enrichingMissing]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;
  const maxLoss = data.top_losses?.[0]?.loss_value || 1;
  return (
    <div className="space-y-6" data-testid="inventory-dashboard">
      {/* Auto-reorder drafts pendientes (zero-noise: solo si hay algo) */}
      <AutoReorderDraftsBanner restaurantId={restaurantId} />

      {/* Pedidos enviados/pendientes esperando recepción */}
      <PendingReceiveBanner restaurantId={restaurantId} />

      {/* Quick actions bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={onOpenPurchases} variant="default" size="sm" className="gap-1.5" data-testid="inv-dash-purchases-btn">
          <ShoppingCart className="w-4 h-4" /> Compras
        </Button>
        <Button onClick={onGoToCount || (() => toast.info("Abre la pestaña Contar"))} variant="outline" size="sm" className="gap-1.5" data-testid="inv-dash-count-btn">
          <ClipboardList className="w-4 h-4" /> Contar
        </Button>
        <Button onClick={onImportInvoice} variant="outline" size="sm" className="gap-1.5" data-testid="inv-dash-invoice-btn">
          <Camera className="w-4 h-4" /> Importar factura
        </Button>
        <Button onClick={onImportCsv} variant="outline" size="sm" className="gap-1.5" data-testid="inv-dash-csv-btn">
          <FileText className="w-4 h-4" /> Importar inventario
        </Button>
        <div className="flex-1" />
        <Button onClick={load} variant="ghost" size="sm" className="gap-1.5" data-testid="inv-dash-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Actualizar
        </Button>
      </div>

      {/* AI Monthly Insight — Fase 7 */}
      <InsightCard
        restaurantId={restaurantId}
        insight={insight}
        loading={insightLoading}
        error={insightError}
        onRefresh={() => loadInsight(true)}
        onOpenVariance={() => setVarianceOpen(true)}
      />

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<Package className="w-4 h-4" />}
          label="Valor de stock"
          value={`€${formatNum(data.total_theoretical_value)}`}
          sub={`${data.ingredient_count} productos`}
          color="cyan"
          sparkline={valueRange === "30d" ? data.value_trend_30d : data.value_trend_7d}
          trendRange={valueRange}
          onTrendRangeChange={setValueRange}
        />
        <KpiCard
          icon={<ShoppingCart className="w-4 h-4" />}
          label="Compras 30d"
          value={`€${formatNum(data.purchase_30d_value)}`}
          sub={`${data.movements_30d_count} movimientos`}
          color="emerald"
        />
        <KpiCard
          icon={<TrendingDown className="w-4 h-4" />}
          label="Merma 30d"
          value={`€${formatNum(data.waste_30d_value)}`}
          sub={`${data.waste_30d_pct}% sobre compras`}
          color={data.waste_30d_pct >= 10 ? "red" : data.waste_30d_pct >= 5 ? "amber" : "emerald"}
          sparkline={wasteRange === "30d" ? data.waste_trend_30d : data.waste_trend_7d}
          trendRange={wasteRange}
          onTrendRangeChange={setWasteRange}
          sparklineMode="bars"
        />
        <KpiCard
          icon={<AlertCircle className="w-4 h-4" />}
          label="Alertas"
          value={data.alerts.length}
          sub={data.alerts.length === 0 ? "Todo en orden" : "Requieren acción"}
          color={data.alerts.length === 0 ? "emerald" : "amber"}
        />
      </div>

      {/* Alerts */}
      {data.alerts.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <h3 className="font-semibold text-sm">Alertas activas</h3>
              <Badge variant="secondary" className="text-[10px]">{data.alerts.length}</Badge>
            </div>
            <div className="space-y-2">
              {data.alerts.map((a, i) => {
                const Icon = ALERT_ICONS[a.severity] || Info;
                return (
                  <div
                    key={i}
                    className={`border rounded-lg p-3 flex items-start gap-3 ${ALERT_STYLES[a.severity]}`}
                    data-testid={`inv-alert-${a.type}`}
                  >
                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{a.title}</p>
                      <p className="text-xs opacity-80 mt-0.5">{a.message}</p>
                    </div>
                    {a.type === "missing_cost" && (
                      <Button
                        variant="outline" size="sm"
                        onClick={handleEnrichMissing}
                        disabled={enrichingMissing}
                        className="flex-shrink-0 text-xs h-7"
                        data-testid="inv-alert-enrich-missing-btn"
                      >
                        {enrichingMissing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        {enrichingMissing ? "Encolando…" : "Calcular con IA"}
                      </Button>
                    )}
                    {a.type === "low_stock" && (
                      <Button variant="outline" size="sm" onClick={onGoToProducts} className="flex-shrink-0 text-xs h-7">
                        Ver
                      </Button>
                    )}
                    {a.type === "high_shrinkage" && onRegisterWaste && (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => onRegisterWaste(a.items?.[0]?.ingredient_id)}
                        className="flex-shrink-0 text-xs h-7 border-destructive/40 text-destructive hover:bg-destructive/10"
                        data-testid="inv-alert-register-waste-btn"
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Registrar merma
                      </Button>
                    )}
                    {a.type === "price_increase" && onGoToProducts && (
                      <Button
                        variant="outline" size="sm"
                        onClick={onGoToProducts}
                        className="flex-shrink-0 text-xs h-7"
                        data-testid="inv-alert-price-increase-btn"
                      >
                        <ShoppingCart className="w-3 h-3 mr-1" /> Revisar precios
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2-column: Top losses + Recent movements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top losses */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="w-4 h-4 text-red-500" />
              <h3 className="font-semibold text-sm">Top 5 pérdidas — últimos 30 días</h3>
            </div>
            {data.top_losses.length === 0 ? (
              <EmptyMsg icon={<CheckCircle2 className="w-8 h-8 text-emerald-500" />}>
                Sin mermas registradas. ¡Buen trabajo!
              </EmptyMsg>
            ) : (
              <div className="space-y-2.5" data-testid="inv-dash-top-losses">
                {data.top_losses.map((t, idx) => (
                  <div key={t.ingredient_id} className="space-y-1 group">
                    <div className="flex items-center justify-between text-sm gap-2">
                      <span className="truncate font-medium flex-1">{idx + 1}. {t.name}</span>
                      <span className="font-mono text-red-700 text-xs whitespace-nowrap">
                        -€{formatNum(t.loss_value)} · {t.loss_qty} {t.unit}
                      </span>
                      {onRegisterWaste && (
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => onRegisterWaste(t.ingredient_id)}
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                          title="Registrar nueva merma para este producto"
                          data-testid={`top-loss-register-${t.ingredient_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-500"
                        style={{ width: `${Math.max(4, (t.loss_value / maxLoss) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent movements */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-cyan-500" />
              <h3 className="font-semibold text-sm">Últimos movimientos</h3>
            </div>
            {data.recent_movements.length === 0 ? (
              <EmptyMsg icon={<Package className="w-8 h-8 text-muted-foreground" />}>
                Aún no hay movimientos registrados.
              </EmptyMsg>
            ) : (
              <div
                className="space-y-1.5 max-h-80 overflow-y-auto pr-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                data-testid="inv-dash-recent-movements"
                tabIndex={0}
                role="region"
                aria-label="Movimientos recientes de inventario"
              >
                {data.recent_movements.map((m) => {
                  const cfg = MOVEMENT_LABELS[m.kind] || { label: m.kind, icon: Package, color: "text-muted-foreground" };
                  const Icon = cfg.icon;
                  const isIn = m.qty > 0;
                  return (
                    <div
                      key={m.movement_id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 text-xs"
                    >
                      <Icon className={`w-3.5 h-3.5 ${cfg.color} flex-shrink-0`} />
                      <span className="font-medium truncate flex-1">{m.ingredient_name}</span>
                      <span className="text-muted-foreground truncate max-w-[80px]">{cfg.label}</span>
                      <span className={`font-mono font-medium tabular-nums ${isIn ? "text-emerald-700" : "text-red-700"}`}>
                        {isIn ? "+" : ""}{m.qty}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <RecentAutoEntriesCard
        restaurantId={restaurantId}
        onOpenDocument={(docId) => {
          // Let the parent decide what to do; default: navigate to finance bandeja
          if (onGoToProducts && typeof window !== "undefined") {
            window.location.assign(`/app/finance?tab=bandeja&doc=${docId}`);
          }
        }}
      />

      <PriceHikesCard
        restaurantId={restaurantId}
        onOpenDocument={(docId) => {
          if (typeof window !== "undefined") {
            window.location.assign(`/app/finance?tab=bandeja&doc=${docId}`);
          }
        }}
      />

      <p className="text-[11px] text-muted-foreground text-right">
        Actualizado: {new Date(data.timestamp).toLocaleTimeString("es-ES")}
      </p>

      {/* Variance alert settings modal (hidden mermas) */}
      <VarianceAlertSettings
        open={varianceOpen}
        onOpenChange={setVarianceOpen}
        restaurantId={restaurantId}
      />
    </div>
  );
}

function EmptyMsg({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      {icon}
      <p className="text-sm text-muted-foreground mt-2">{children}</p>
    </div>
  );
}

function formatNum(v) {
  return (v || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default InventoryDashboard;
